import
    {
        IDocumentChangeHandler,
        IListener,
        IOfficeHost,
    } from "./office-host.types";
import { DOCUMENT_UID_KEY } from "@core/shared/office-hosts/office-hosts-constants";
import { environment } from "@env";
import * as cmmn from '@utils/index';
import Office from "@core/shared/office.instance";
import { isDevMode } from '@angular/core';
import { logger } from "@services/logger/logger.service";
import { v4 as uuidV4 } from 'uuid';

// Strips query/hash so ephemeral session tokens on the URL (Office on the web
// often appends `?web=1`, ui locale params, etc.) don't split the id for the
// same file. Word desktop and web resolve the same cloud file to the same
// origin+path, so this is stable across platforms - unlike the settings-UID
// scheme below, which is generated independently per client session.
function normalizeDocumentUrl(url: string): string
{
    try
    {
        const parsed = new URL(url);
        parsed.search = '';
        parsed.hash = '';
        const path = parsed.pathname.replace(/\/+$/, '') || '/';
        return `${ parsed.origin.toLowerCase() }${ path }`;
    }
    catch
    {
        return url.trim();
    }
}

async function hashDocumentUrl(url: string): Promise<string>
{
    const normalized = normalizeDocumentUrl(url);
    const data = new TextEncoder().encode(normalized);
    const digestBuffer = await crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(digestBuffer))
        .map((byte) => byte.toString(16).padStart(2, '0'))
        .join('');
}

export type TListenersMap = Map<
    Office.EventType,
    IListener[]
>;

export abstract class BaseHost implements IOfficeHost
{
    protected isReady = false;
    protected platform: Office.PlatformType = Office.PlatformType.PC;
    protected eventListeners: TListenersMap = new Map<Office.EventType, IListener[]>();
    private isDevMode: boolean = isDevMode();

    constructor()
    {
        Office.onReady((info) =>
        {
            this.platform = info.platform;
            this.isReady = true;
        });
    }

    async waitReadyAsync(): Promise<void>
    {
        let max_count = 100; // wait 10 seconds
        while (!this.isReady && max_count-- > 0)
        {
            await cmmn.sleep(100);
        }

        if (max_count == 0)
        {
            throw new Error(`timeout has expired`);
        }
    }

    public async getDocumentIDAsync(): Promise<string>
    {
        if (this.isDevMode)
        {
            throw new Error("Method not implemented.");
        }

        return '';
    }

    // Id derived from the file's own storage location (via getFilePropertiesAsync),
    // so it agrees across Office on the web and Office desktop for the same cloud
    // file. Null when the document has no url yet (e.g. unsaved local file) -
    // callers should fall back to resolveSettingsDocumentIDAsync in that case.
    protected async resolveFileUrlDocumentIDAsync(): Promise<string | null>
    {
        const url = await cmmn.callAsync<string | null>(async (r) =>
        {
            Office.context.document.getFilePropertiesAsync((result: Office.AsyncResult<Office.FileProperties>) =>
            {
                if (result.status == Office.AsyncResultStatus.Failed || !result.value?.url)
                {
                    r.next(null);
                }
                else
                {
                    r.next(result.value.url);
                }
            });
        });

        if (!url)
        {
            return null;
        }

        return hashDocumentUrl(url);
    }

    // Legacy scheme: a random id minted on first use and persisted into the
    // document's own settings. Kept only as a fallback for documents without a
    // url - it's generated independently per client session, which is why the
    // same document can end up with different ids in Office on the web vs.
    // Office desktop.
    protected async resolveSettingsDocumentIDAsync(): Promise<string | undefined>
    {
        let documentID = Office.context.document.settings.get(DOCUMENT_UID_KEY);

        if (!documentID)
        {
            documentID = uuidV4();
            Office.context.document.settings.set(DOCUMENT_UID_KEY, documentID);

            const isSaved = await cmmn.callAsync<boolean>(async (r) =>
            {
                Office.context.document.settings.saveAsync((saveResult: Office.AsyncResult<void>) =>
                {
                    if (saveResult.status == Office.AsyncResultStatus.Failed)
                    {
                        logger.error(`error code: ${ saveResult.error.code }, name: ${ saveResult.error.name }, message: '${ saveResult.error.message }'`);
                        r.next(false);
                    } else
                    {
                        r.next(true);
                    }
                });
            });

            if (!isSaved)
            {
                documentID = undefined;
            }
        }

        return documentID;
    }

    public async setSelectedTextAsync(plainText: string): Promise<void>
    {
        if (this.isDevMode)
        {
            throw new Error("Method not implemented.");
        }
    }

    public async setSelectedFormattedTextAsync(plainText: string, html: string): Promise<void>
    {
        void html;
        await this.setSelectedTextAsync(plainText);
    }

    public async setSelectedImageAsync(fileName: string, b64Text: string): Promise<void>
    {
        if (this.isDevMode)
        {
            throw new Error("Method not implemented.");
        }
    }

    public checkDocumentReadable(): boolean { return false; }
    public async getDocumentTextAsync(): Promise<string>
    {
        if (this.isDevMode)
        {
            throw new Error("Method not implemented.");
        }

        return '';
    }
    public async getDocumentImageAsync(): Promise<string>
    {
        return '';
    }
    public checkSelectable(): boolean { return false; }
    public checkEditable(): boolean { return false; }
    public getSelectedContentAsync(): Promise<string> { throw new Error('not implemented'); };
    public async onDocumentChange(handler: IDocumentChangeHandler): Promise<void>
    {
        if (this.isDevMode)
        {
            throw new Error('Method not implemented');
        }
    }
    public async attachImageToEmail(base64Image: string, filename: string): Promise<void>
    {
        if (this.isDevMode)
        {
            throw new Error('Method not implemented');
        }
    }

    public attachHandler(
        eventType: Office.EventType,
        eventHandler: IListener,
    ): void
    {
        if (!this.eventListeners.get(eventType))
        {
            this.eventListeners.set(eventType, [ eventHandler ]);
        }
        else
        {
            const list = this.eventListeners.get(eventType) as IListener[];
            list.push(eventHandler);
        }
    }

    public detachHandler(eventType: Office.EventType, eventHandler: IListener): void
    {
        const listeners = this.eventListeners.get(eventType);
        if (!listeners)
        {
            return;
        }
        const index = listeners.indexOf(eventHandler, 0);
        if (index > -1)
        {
            listeners.splice(index, 1);
        }
    }

    public getTag(): string
    {
        return 'global';
    }
}
